import React, { useEffect, useMemo, useState } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Button, Chip, colors } from '@dhis2/ui';
import { ArrowLeft, Printer, FileDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { POSITION_ATTRIBUTES } from '../auth/utils/reportingStructure';

const shellStyles = {
  page: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  hero: {
    background: 'linear-gradient(135deg, #0f5132 0%, #1d8348 58%, #f4d03f 100%)',
    color: '#fff',
    borderRadius: '18px',
    padding: '24px',
    boxShadow: '0 18px 40px rgba(10, 63, 37, 0.18)',
  },
  heroTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
  },
  heroTitle: {
    margin: 0,
    fontSize: '1.2rem',
    lineHeight: 1.1,
  },
  heroText: {
    margin: '8px 0 0',
    maxWidth: '780px',
    color: 'rgba(255,255,255,0.9)',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '16px 18px',
    backgroundColor: '#fff',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '14px',
    boxShadow: '0 8px 22px rgba(17, 24, 39, 0.06)',
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '18px',
    padding: '12px',
    border: `1px solid ${colors.grey300}`,
    boxShadow: '0 12px 30px rgba(17, 24, 39, 0.08)',
  },
};

const printStyles = `
  @page {
    margin: 16mm 12mm 24mm;
  }

  @media print {
    html,
    body,
    #root {
      height: auto !important;
      overflow: visible !important;
      background: #fff !important;
    }

    body {
      background: #fff !important;
    }

    .report-shell__toolbar,
    .report-shell__hero-actions,
    [data-print-hidden="true"] {
      display: none !important;
    }

    .report-shell__page {
      display: block !important;
      gap: 0 !important;
      overflow: visible !important;
    }

    .report-shell__page,
    .report-shell__content {
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
      background: #fff !important;
      overflow: visible !important;
    }

    .report-shell__content {
      padding-bottom: 120px !important;
    }

    .report-shell__content,
    .report-shell__content > * {
      overflow: visible !important;
      max-height: none !important;
    }

    .report-shell__hero {
      box-shadow: none !important;
      break-inside: avoid;
      margin-bottom: 16px !important;
    }

    .report-shell__content button,
    .report-shell__content select,
    .report-shell__content input[type="checkbox"] {
      display: none !important;
    }

    .report-shell__print-footer {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex !important;
      justify-content: space-between;
      gap: 24px;
      padding: 10px 12mm 0;
      background: #fff;
      border-top: 1px solid #d9dee5;
    }

    .report-shell__signature-placeholder {
      min-height: 28px;
      margin-bottom: 4px;
    }
  }
`;

const getAttributeValue = (position, attributeId) =>
  position?.extractedAttributes?.[attributeId] ||
  position?.attributes?.find((attribute) => attribute.attribute === attributeId)?.value ||
  '';

export default function ReportLayout({
  title,
  description,
  badge,
  children,
}) {
  const navigate = useNavigate();
  const engine = useDataEngine();
  const { user, reportingToPosition } = useAuth();
  const [approvedByName, setApprovedByName] = useState('');

  const preparedByName = useMemo(
    () => user?.displayName || user?.name || user?.username || 'Prepared by',
    [user]
  );

  useEffect(() => {
    let isActive = true;

    const loadApprover = async () => {
      const managerUserId = getAttributeValue(reportingToPosition, POSITION_ATTRIBUTES.CURRENT_INCUMBENT);
      if (!managerUserId) {
        if (isActive) setApprovedByName('');
        return;
      }

      try {
        const response = await engine.query({
          approvedBy: {
            resource: `users/${managerUserId}`,
            params: {
              fields: 'id,displayName,name,username',
            },
          },
        });

        if (isActive) {
          setApprovedByName(
            response?.approvedBy?.displayName ||
              response?.approvedBy?.name ||
              response?.approvedBy?.username ||
              ''
          );
        }
      } catch (error) {
        if (isActive) setApprovedByName('');
      }
    };

    loadApprover();

    return () => {
      isActive = false;
    };
  }, [engine, reportingToPosition]);

  return (
    <>
      <style>{printStyles}</style>
      <div className="report-shell__page" style={shellStyles.page}>
        <section className="report-shell__hero" style={shellStyles.hero}>
          <div style={shellStyles.heroTop}>
            <div>
              {badge ? (
                <Chip backgroundColor="rgba(255,255,255,0.18)" color="#fff">
                  {badge}
                </Chip>
              ) : null}
              <h1 style={shellStyles.heroTitle}>{title}</h1>
              {description ? <p style={shellStyles.heroText}>{description}</p> : null}
            </div>
            <div
              className="report-shell__hero-actions"
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            >
              <Button
                small
                onClick={() => navigate('/tasks')}
                icon={<ArrowLeft size={16} />}
              >
                Back to reports
              </Button>
            </div>
          </div>
        </section>

        <section className="report-shell__toolbar" style={shellStyles.toolbar}>
          <Chip backgroundColor={colors.green100} color={colors.green900}>
            Ready for PDF export
          </Chip>
          <span style={{ color: colors.grey700, fontSize: '0.95rem' }}>
            Use your browser print dialog and choose Save as PDF.
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button small onClick={() => window.print()} icon={<Printer size={16} />}>
              Print report
            </Button>
            <Button primary small onClick={() => window.print()} icon={<FileDown size={16} />}>
              Save as PDF
            </Button>
          </div>
        </section>

        <section className="report-shell__content" style={shellStyles.content}>
          {children}
        </section>

        <footer
          className="report-shell__print-footer"
          style={{ display: 'none', alignItems: 'flex-end', color: '#102a43' }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px' }}>Prepared by</div>
            <div className="report-shell__signature-placeholder" />
            <div style={{ borderTop: '1px solid #9fb3c8', paddingTop: '4px', fontSize: '0.82rem' }}>
              {preparedByName}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px' }}>Approved by</div>
            <div className="report-shell__signature-placeholder" />
            <div style={{ borderTop: '1px solid #9fb3c8', paddingTop: '4px', fontSize: '0.82rem' }}>
              {approvedByName || 'Pending approval'}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
